import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { formatCurrency, formatNumber } from "@/lib/format";
import type { MembershipPlan } from "@/types/club";

interface Form {
  name: string; 
  description: string;
  price: string; 
  points_awarded: string; // 🛠️ Fixed
}

export function MembershipAdminPage() {
  const [openModal, setOpenModal] = useState(false);
  const qc = useQueryClient();

  const plans = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => get<any[]>("/membership/plans"),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<Form>();

  const create = useMutation({
    mutationFn: (d: Form) => post<any>("/admin/membership/plans", {
      name: d.name,
      description: d.description,
      price: d.price,
      points_awarded: parseInt(d.points_awarded), // 🛠️ Fixed
    }),
    onSuccess: () => {
      toast.success("Point Package created");
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      reset();
      setOpenModal(false);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Point Packages</h1>
          <p className="text-sm text-surface-500 mt-1">
            Packages that customers can buy to directly gain Qualification Points.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setOpenModal(true)}>
          New Package
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>All Packages</CardTitle></CardHeader>
        <CardBody className="p-0">
          {plans.isLoading ? (
            <div className="p-5"><Skeleton className="h-14" /></div>
          ) : plans.data && plans.data.length ? (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH><TH>Price</TH><TH>Points Given</TH><TH>Status</TH>
                </TR>
              </THead>
              <tbody>
                {plans.data.map((p) => (
                  <TR key={p.id}>
                    <TD className="font-medium">{p.name}</TD>
                    <TD className="font-bold">{formatCurrency(p.price)}</TD>
                    <TD className="font-mono text-brand-600 font-bold">{formatNumber(p.points_awarded)} Pts</TD>
                    <TD><StatusPill status={p.status} /></TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="p-8 text-center text-sm text-surface-500">No packages yet.</div>
          )}
        </CardBody>
      </Card>

      <Modal open={openModal} onClose={() => setOpenModal(false)} title="New Point Package">
        <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-4">
          <Input label="Package name" placeholder="e.g. 100 Points Pack"
                 error={errors.name?.message} {...register("name", { required: "Required" })} />
          <div>
            <label className="block text-sm font-medium text-surface-800 mb-1.5">Description</label>
            <textarea {...register("description")} className="w-full h-20 px-3.5 py-2.5 bg-white border border-surface-300 rounded-lg text-sm resize-none focus:border-brand-500 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Price (Tk)" type="number" step="0.01"
                   error={errors.price?.message} {...register("price", { required: "Required" })} />
            <Input label="Points to Award" type="number"
                   error={errors.points_awarded?.message} {...register("points_awarded", { required: "Required", min: 1 })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setOpenModal(false)}>Cancel</Button>
            <Button type="submit" loading={create.isPending}>Create Package</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}